import { Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Category } from 'src/app/model/category';
import { HomeConfig } from 'src/app/model/homeConfig';
import { ProductService } from 'src/app/pages/products/services/product.service';
import { HomeService } from '../../services/home.service';
import { AppModeService } from 'src/app/services/app-mode.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-home-app-top',
  templateUrl: './home-app-top.component.html',
  styleUrls: ['./home-app-top.component.scss'],
})
export class HomeAppTopComponent implements OnInit {
  @Input() config!: HomeConfig;
  imageUrl = environment.apiUrl;
  categories: Category[] = [];
  isFilterOpen = false;
  selectedColors: string[] = [];
  selectedSizes: string[] = [];
  selectedSort = '';
  selectedCategory = '';
  colors: string[] = [];
  sizes: string[] = [];

  readonly sortOptions = [
    { value: '', label: 'Default' },
    { value: 'NAME_ASC', label: 'Name A-Z' },
    { value: 'NAME_DESC', label: 'Name Z-A' },
    { value: 'PRICE_ASC', label: 'Price Low-High' },
    { value: 'PRICE_DESC', label: 'Price High-Low' },
  ];

  catalogueResponsiveOptions = [
    {
      breakpoint: '1024px',
      numVisible: 2,
      numScroll: 1,
    },
    {
      breakpoint: '768px',
      numVisible: 1,
      numScroll: 1,
    },
  ];

  constructor(
    private readonly homeService: HomeService,
    private readonly productService: ProductService,
    private readonly router: Router,
    public readonly appModeService: AppModeService,
  ) {}

  ngOnInit(): void {
    this.loadFilterOptions();
  }

  /**
   * Load filter options (categories, colors, sizes)
   * Colors and sizes are only loaded for web mode
   * Mobile app view doesn't display these filters, so we skip loading them for performance
   */
  private loadFilterOptions(): void {
    const isAppMode = this.appModeService.isApp();

    // Load categories (used in both modes)
    this.homeService.getCategories().subscribe((res) => {
      this.categories = res;
    });

    // Load colors only for web mode
    if (!isAppMode) {
      this.productService.getAllColors().subscribe((res) => {
        if (res) {
          this.colors = res;
        }
      });
    }

    // Load sizes only for web mode
    if (!isAppMode) {
      this.productService.getAllSizes().subscribe((res) => {
        if (res) {
          this.sizes = this.sortSizes(res);
        }
      });
    }
  }

  get catalogueImages(): string[] {
    return this.config?.catalogueSliderImageUrls ?? [];
  }

  /**
   * Navigate to products filtered by category
   */
  goToProductsByCategory(categoryName: string): void {
    this.router.navigate(['/products'], {
      queryParams: { category: categoryName },
    });
  }

  /**
   * Open filter modal
   */
  openFilter(): void {
    this.isFilterOpen = true;
  }

  /**
   * Close filter modal
   */
  closeFilter(): void {
    this.isFilterOpen = false;
  }

  /**
   * Toggle color selection
   */
  toggleColor(color: string): void {
    if (this.selectedColors.includes(color)) {
      this.selectedColors = this.selectedColors.filter(
        (item) => item !== color,
      );
      return;
    }
    this.selectedColors = [...this.selectedColors, color];
  }

  /**
   * Toggle size selection
   */
  toggleSize(size: string): void {
    if (this.selectedSizes.includes(size)) {
      this.selectedSizes = this.selectedSizes.filter((item) => item !== size);
      return;
    }
    this.selectedSizes = [...this.selectedSizes, size];
  }

  /**
   * Clear all selected filters
   */
  clearFilters(): void {
    this.selectedColors = [];
    this.selectedSizes = [];
    this.selectedSort = '';
    this.selectedCategory = '';
  }

  /**
   * Apply selected filters and navigate to products page
   */
  applyFilters(): void {
    const queryParams: Record<string, string | string[] | null> = {
      category: this.selectedCategory || null,
      colors: this.selectedColors.length ? this.selectedColors : null,
      sizes: this.selectedSizes.length ? this.selectedSizes : null,
      sort: this.selectedSort || null,
    };

    this.closeFilter();
    this.router.navigate(['/products'], { queryParams });
  }

  /**
   * TrackBy function for category list rendering optimization
   */
  trackByCategory(_: number, category: Category): string {
    return category.id || category.name;
  }

  /**
   * TrackBy function for catalogue images rendering optimization
   */
  trackByCatalogueImage(_: number, imagePath: string): string {
    return imagePath;
  }

  /**
   * Get category image with fallback
   */
  getCategoryImage(category: Category): string {
    return category.imageUrl
      ? this.imageUrl + category.imageUrl
      : 'assets/img/jersey.png';
  }

  /**
   * Sort sizes in a logical order
   * Handles both standard sizes (XS, S, M, L, XL, XXL) and year-based sizes
   */
  private sortSizes(sizes: string[]): string[] {
    const normalizedSizes = (sizes || [])
      .map((size: string | null) => (size || '').trim())
      .filter((size: string) => Boolean(size));

    const sizeOrder = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL', '4XL'];

    const yearsSizes = normalizedSizes
      .filter((size: string) => size.toLowerCase().includes('year'))
      .sort((a: string, b: string) => {
        const numA = parseInt(a, 10);
        const numB = parseInt(b, 10);
        return isNaN(numA) || isNaN(numB) ? a.localeCompare(b) : numA - numB;
      });

    const regularSizes = normalizedSizes
      .filter((size: string) => !size.toLowerCase().includes('year'))
      .sort((a: string, b: string) => {
        const labelA = a.split('=')[0].trim().replace(/\s/g, '').toUpperCase();
        const labelB = b.split('=')[0].trim().replace(/\s/g, '').toUpperCase();
        const indexA = sizeOrder.indexOf(labelA);
        const indexB = sizeOrder.indexOf(labelB);

        if (indexA === -1 && indexB === -1) return labelA.localeCompare(labelB);
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;

        return indexA - indexB;
      });

    return [...yearsSizes, ...regularSizes];
  }
}
